import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useActiveDraftCart } from '../hooks/useActiveDraftCart';

/**
 * Small TopNav pill that surfaces the customer's in-progress draft cart.
 *
 * Hidden when:
 *   - the user is not authenticated
 *   - there is no active draft id in sessionStorage
 *   - the active draft has zero items (race-window during cart-clear)
 *
 * On count increment: the pill performs a brief "happy bounce" + a "+1"
 * floats out of the count chip — a tiny dopamine hit that makes the
 * Add-New-Item flow feel rewarding (and nudges multi-item orders).
 *
 * Click → /order-summary with the orderId in router state so
 * OrderSummaryPage's existing fetch path picks it up immediately.
 */
const formatMoney = (amount, currency) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'CAD',
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  } catch {
    return `$${Number(amount).toFixed(0)}`;
  }
};

// Hoisted to module scope to keep referential stability across renders
// (Framer-Motion otherwise re-subscribes to a new variants object each
// time and our happy-bounce gets clobbered).
const POP_ANIMATE = {
  scale: [1, 1.18, 0.96, 1.04, 1],
  rotate: [0, -4, 4, -2, 0],
  transition: { duration: 0.55, ease: 'easeInOut' },
};
const FLOATER_INITIAL = { opacity: 0, y: 6, scale: 0.6 };
const FLOATER_ANIMATE = { opacity: 1, y: -22, scale: 1 };
const FLOATER_EXIT = { opacity: 0, y: -32, scale: 0.9 };
const FLOATER_TRANSITION = { duration: 0.85, ease: 'easeOut' };

export const CartBadge = ({ routeKey, isAuthenticated }) => {
  const navigate = useNavigate();
  const { orderId, itemCount, totalPrice, currency } = useActiveDraftCart({
    routeKey,
    isAuthenticated,
  });

  // Track previous count to fire the pop ONLY on increments — not on
  // remove-item, not on initial mount, not on order-fetch refresh.
  const prevCountRef = useRef(itemCount);
  // Increments every time we want a new pop animation cycle. Used as
  // the Framer-Motion key so the bounce sequence re-runs cleanly.
  const [popTick, setPopTick] = useState(0);
  // Transient flag controlling the "+1" floater. Cleared by a timeout
  // matching the floater's exit animation duration.
  const [showFloater, setShowFloater] = useState(false);

  useEffect(() => {
    if (itemCount > prevCountRef.current && prevCountRef.current >= 0) {
      setPopTick((n) => n + 1);
      setShowFloater(true);
      const t = setTimeout(() => setShowFloater(false), 900);
      prevCountRef.current = itemCount;
      return () => clearTimeout(t);
    }
    prevCountRef.current = itemCount;
  }, [itemCount]);

  if (!isAuthenticated || !orderId || itemCount <= 0) return null;

  const label = `${itemCount} item${itemCount !== 1 ? 's' : ''}`;
  return (
    <motion.button
      type="button"
      onClick={() => navigate('/order-summary', { state: { orderId } })}
      data-testid="nav-cart-badge"
      data-cart-count={itemCount}
      title={`${label} in your cart · ${formatMoney(totalPrice, currency)} · click to view`}
      // `key` on the animate prop forces Framer-Motion to re-run the
      // sequence each time popTick increments. Without keying, the same
      // animation object would be a no-op on subsequent ticks.
      animate={popTick > 0 ? POP_ANIMATE : undefined}
      whileTap={{ scale: 0.94 }}
      key={`cart-pop-${popTick}`}
      className="relative flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#6FBF73] hover:bg-[#5AAB66] text-[#14281D] text-xs font-bold shrink-0 shadow-sm"
    >
      <span className="relative inline-flex">
        <ShoppingCart className="w-4 h-4" />
        <span
          className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-500 text-[10px] font-extrabold text-[#14281D] ring-2 ring-[#14281D] flex items-center justify-center leading-none"
          aria-label={label}
          data-testid="nav-cart-count"
        >
          {itemCount > 99 ? '99+' : itemCount}
        </span>
        {/* "+1" floater — fires once per increment, then unmounts. */}
        <AnimatePresence>
          {showFloater && (
            <motion.span
              key={`cart-floater-${popTick}`}
              initial={FLOATER_INITIAL}
              animate={FLOATER_ANIMATE}
              exit={FLOATER_EXIT}
              transition={FLOATER_TRANSITION}
              className="pointer-events-none absolute -top-1 -right-3 text-[11px] font-extrabold text-amber-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]"
              aria-hidden="true"
              data-testid="nav-cart-floater"
            >
              +1
            </motion.span>
          )}
        </AnimatePresence>
      </span>
      <span className="hidden md:inline">Cart</span>
      <span className="hidden lg:inline text-amber-900/70 font-mono">
        · {formatMoney(totalPrice, currency)}
      </span>
    </motion.button>
  );
};

export default CartBadge;
